// FIXME: remove eventually

import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useParams } from 'react-router-dom';

import { ChildStepProgressLabel } from '@/components/molecules/ChildStepProgressLabel';
import { ParentStepProgressLabel } from '@/components/molecules/ParentStepProgressLabel';
import { ChildStep, Status } from '@/types/steps';

interface Props {
  readonly path: string;
  readonly status?: Status;
  readonly title: string;
  // FIXME: make `any` `T` and make the interface generic
  readonly steps?: readonly ChildStep<any>[];
  readonly separator?: React.ReactElement | null;

  readonly statuses: Record<string, Status>;
}

export const StepProgressLabel: React.FC<React.PropsWithChildren<Props>> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const { path, title, steps = [], statuses, separator = null } = props;

  const { parentId, childId: _ } = useParams<{
    readonly parentId: string;
    readonly childId: string;
  }>();

  const expanded = React.useMemo((): boolean => path === parentId, [path, parentId]);
  const parentPath = React.useMemo((): string => {
    return [path, steps[0]?.path]
      .filter((segment: string | undefined): segment is string => !!segment)
      .join('/');
  }, [path, steps]);
  const completed = React.useMemo((): boolean => {
    return steps.every((child: ChildStep<any>): boolean => {
      return (statuses[child.type] & Status.completed) === Status.completed;
    });
  }, [statuses, steps]);

  const parentStatus = React.useMemo((): Status => {
    if (expanded) {
      return completed ? Status.completed : Status.initial;
    }

    return Status.initial | (completed ? Status.completed : Status.initial);
  }, [completed, expanded]);

  return (
    <>
      <ParentStepProgressLabel path={parentPath} title={title} status={parentStatus} />

      {/* If there's just 1 child, then it doesn't make sense to show it */}
      {expanded && steps.length > 1 ? (
        <Box sx={styles.children}>
          {steps.map((child: ChildStep<any>): React.ReactElement => {
            return (
              <ChildStepProgressLabel
                key={child.path}
                status={statuses[child.type]}
                path={child.path}
                title={child.title}
              />
            );
          })}
          {separator}
        </Box>
      ) : (
        separator
      )}
    </>
  );
};

const styles: Record<string, SystemStyleObject> = {
  child: {
    display: 'flex',
    alignItems: 'center',
  },
  children: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    py: 1,
    mt: '4px',
    pl: 2,
  },
};
